import { ArrowLeftIcon, MagnifyingGlassIcon, MapPinIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { formatAddress, toTitleCase } from "@utils/strings";
import clsx from "clsx";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDebounce } from "use-debounce";

import { Button, Heading, Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  type GetClinicListingsOutput,
  type GetOrganizationsOutput,
  getBasecampClinicListingsRetrieveQueryKey,
  useBasecampClinicListingsLinkCreate,
  useBasecampClinicListingsRetrieve,
  useBasecampOrganizationsRetrieve,
} from "@api/rest/types-and-hooks";
import { Breadcrumb } from "@components/Breadcrumb/Breadcrumb";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

import { AddEditClinicForm } from "./AddEditClinicForm";

export const AddClinicFlow: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();

  const { data: orgData } = useBasecampOrganizationsRetrieve({ id: orgId }, { query: { enabled: !!orgId } });
  const org: GetOrganizationsOutput | undefined =
    orgData?.status === 200
      ? Array.isArray(orgData.data)
        ? orgData.data.find((o) => String(o.id) === orgId)
        : orgData.data
      : undefined;
  const orgName = org?.name;
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const [selectedClinic, setSelectedClinic] = useState<GetClinicListingsOutput | null>(null);
  const [showManualForm, setShowManualForm] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const queryClient = useQueryClient();
  const { mutateAsync: linkClinic } = useBasecampClinicListingsLinkCreate();

  const { data: searchData, isLoading: isSearching } = useBasecampClinicListingsRetrieve(
    { search_term: debouncedSearchTerm, is_claimed: false },
    { query: { enabled: !!debouncedSearchTerm } },
  );
  const searchResults: GetClinicListingsOutput[] =
    searchData?.status === 200 ? (Array.isArray(searchData.data) ? searchData.data : [searchData.data]) : [];

  const handleClaimClinic = async () => {
    if (!selectedClinic) return;
    setIsClaiming(true);
    try {
      const response = await linkClinic({
        data: { clinic_listing_id: selectedClinic.id, org_id: orgId! },
      });

      if (response.status !== 204) {
        notifyError("Failed to submit request. Please try again.");
        return;
      }

      notifySuccess("Clinic request submitted successfully");
      queryClient.invalidateQueries({ queryKey: getBasecampClinicListingsRetrieveQueryKey() });
      navigate(`/organizations/${orgId}?tab=clinic-listings`);
    } finally {
      setIsClaiming(false);
    }
  };

  const breadcrumbCrumbs = [
    { label: "Organizations", to: `/organizations/${orgId}` },
    ...(orgName ? [{ label: orgName, to: `/organizations/${orgId}` }] : []),
    { label: "Add clinic" },
  ];

  // Pending / success view
  const isDebouncing = !!searchTerm && searchTerm !== debouncedSearchTerm;
  const showResults = !!debouncedSearchTerm && !isSearching && !isDebouncing && searchResults.length > 0;
  const showNoResults = !!debouncedSearchTerm && !isSearching && !isDebouncing && searchResults.length === 0;
  const showSkeleton = !!searchTerm && (isSearching || isDebouncing);

  const selectedClinicAddress =
    selectedClinic && selectedClinic.location
      ? formatAddress({
          addressLine1: toTitleCase(selectedClinic.location.address_line_1) || undefined,
          addressLine2: toTitleCase(selectedClinic.location.address_line_2 ?? "") || undefined,
          city: toTitleCase(selectedClinic.location.city),
          state: selectedClinic.location.state,
          zip: selectedClinic.location.postal_code,
        })
      : null;

  // Search view (default)
  return (
    <div className="flex flex-col py-8 items-center bg-bg-default px-4 sm:px-6 lg:px-8">
      <div className="min-w-[70vw]">
        <Breadcrumb crumbs={breadcrumbCrumbs} />
        <Heading size="medium">Add clinic</Heading>
        <Paragraph size="small" colorType="secondary">
          Search for a clinic in the directory. If it&apos;s not listed, you can add it.
        </Paragraph>

        <div className={clsx(!showManualForm && "mt-6 rounded-xl border border-border-medium bg-bg-surface")}>
          {showManualForm ? (
            <AddEditClinicForm orgId={orgId!} onCancel={() => setShowManualForm(false)} />
          ) : selectedClinic ? (
            <div className="flex flex-col gap-3 p-6">
              <button
                type="button"
                onClick={() => setSelectedClinic(null)}
                className="flex flex-row items-center gap-2 w-fit hover:opacity-90 transition-opacity"
              >
                <ArrowLeftIcon className="size-4 text-rio-600" style={{ strokeWidth: 2 }} />
                <Paragraph className="!text-rio-600" displayType="loud" size="small">
                  Back to search
                </Paragraph>
              </button>
              <Paragraph colorType="primary" size="medium" displayType="loud">
                Add {selectedClinic.name} to {orgName}
              </Paragraph>
              <div className="flex flex-col">
                <div className="flex flex-col p-4 gap-2 border rounded-xl bg-limestone-50">
                  <div className="flex flex-col">
                    <Label size="small" colorType="secondary">
                      Name
                    </Label>
                    <Paragraph colorType="secondary">{selectedClinic.name}</Paragraph>
                  </div>
                  {selectedClinicAddress && (
                    <div className="flex flex-col">
                      <Label size="small" colorType="secondary">
                        Address
                      </Label>
                      <Paragraph colorType="secondary">{selectedClinicAddress}</Paragraph>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-row justify-end gap-2">
                <Button
                  type="button"
                  variant="primary"
                  buttonStyle="border"
                  size="md"
                  text="Cancel"
                  onClick={() => setSelectedClinic(null)}
                  disabled={isClaiming}
                />
                <Button
                  type="button"
                  variant="primary"
                  buttonStyle="filled"
                  size="md"
                  text={isClaiming ? "Adding..." : "Add"}
                  onClick={handleClaimClinic}
                  disabled={isClaiming}
                />
              </div>
            </div>
          ) : (
            <div className="p-8">
              <div className="flex flex-col gap-1 mb-8">
                <Paragraph size="small">Search for a clinic</Paragraph>
                <div className="relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-text-tertiary">
                    <MagnifyingGlassIcon className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search clinic by name, city or zip"
                    className="w-full pl-9 pr-3 py-2 text-sm border border-border-subtle rounded-md bg-bg-surface h-10 focus:outline-none focus:ring-0 focus:border-border-subtle"
                  />
                </div>
              </div>

              {/* Skeleton loading */}
              {showSkeleton && (
                <div className="flex flex-col gap-2 mt-6">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-[72px] w-full rounded-xl bg-limestone-200 animate-pulse" />
                  ))}
                </div>
              )}

              {/* No results */}
              {showNoResults && (
                <div className="flex flex-col items-center gap-2 mt-6">
                  <Paragraph colorType="secondary" displayType="loud">
                    No clinics found
                  </Paragraph>
                  <Paragraph size="small" colorType="secondary">
                    We couldn&apos;t find any clinics matching &quot;{searchTerm}&quot;
                  </Paragraph>
                  <Button
                    variant="primary"
                    buttonStyle="filled"
                    size="sm"
                    text="Manually add clinic"
                    onClick={() => setShowManualForm(true)}
                  />
                </div>
              )}

              {/* Results */}
              {showResults && (
                <div className="flex flex-col gap-2 mt-6">
                  <Paragraph size="small" colorType="secondary">
                    Found {searchResults.length} clinic{searchResults.length !== 1 ? "s" : ""}
                  </Paragraph>

                  <div className="flex flex-col gap-2 max-h-[35vh] overflow-y-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]">
                    {searchResults.map((result) => {
                      const address = result.location
                        ? formatAddress({
                            addressLine1: toTitleCase(result.location.address_line_1) || undefined,
                            addressLine2: toTitleCase(result.location.address_line_2 ?? "") || undefined,
                            city: toTitleCase(result.location.city),
                            state: result.location.state,
                            zip: result.location.postal_code,
                          })
                        : null;
                      return (
                        <div
                          key={result.id}
                          className="flex flex-row justify-between p-4 border border-border-medium rounded-xl"
                        >
                          <div className="flex flex-col">
                            <Paragraph displayType="loud" colorType="primary">
                              {result.name}
                            </Paragraph>
                            {address && (
                              <div className="flex flex-row gap-2 items-center">
                                <MapPinIcon className="size-5 text-text-secondary shrink-0" />
                                <Paragraph colorType="secondary">{address}</Paragraph>
                              </div>
                            )}
                          </div>
                          <Button
                            variant="primary"
                            buttonStyle="border"
                            size="sm"
                            text="Select"
                            onClick={() => setSelectedClinic(result)}
                          />
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex flex-row justify-between bg-bg-default p-4 border border-dashed border-border-medium rounded-xl">
                    <div className="flex flex-col">
                      <Paragraph displayType="loud" colorType="primary">
                        Can&apos;t find your clinic?
                      </Paragraph>
                      <Paragraph colorType="secondary">Add a new clinic to our directory</Paragraph>
                    </div>
                    <Button
                      variant="primary"
                      buttonStyle="filled"
                      size="sm"
                      text="Add new"
                      onClick={() => setShowManualForm(true)}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
